import React from 'react';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Select2Wrapper from './Select2Wrapper';

// MUI transitions
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import { ATTACHABLES, ATTACHABLES_OPTIONS } from '../constants/AttachmentConstants';
import { FIT_STATUS_OPTIONS } from '../constants/ehc/ParticipantConstants';
import CustomFileUpload from './CustomFileUpload';

export default class AttachmentDataForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      attachment: { ...props.attachment },
      hospitalOptions: [],
      doctorOptions: [],
      doctorsHospitalId: null,
      profileOptions: [],
      attachableOptions: [],
      attachablesType: null,
      attachablesProfileId: null,
      confirming: false,
      submitting: false,
      submitError: null,
    };
  }

  errorText = (result) => {
    if (!result) return 'Upload failed. Please try again.';
    if (typeof result.errors === 'string') return result.errors;
    if (Array.isArray(result.errors)) return result.errors.join(', ');
    return result.message || 'Upload failed. Please try again.';
  }

  BASE_URL = `/api/attachments`;

  handleChange = (...args) => {
    let newAttachmentData = this.state.attachment || {};
    for (let i = 0; (i + 1) < args.length; i += 2) {
      const field = args[i];
      const value = args[i + 1];
      newAttachmentData[field] = value;
    }
    this.setState({ attachment: newAttachmentData });
  }

  handleAttachmentRequest = async (event) => {
    event.preventDefault();

    const attachment = this.state.attachment;
    if (!this.props.editable || !attachment) return;

    const csrfToken = document.querySelector('[name=csrf-token]').content;
    const formData = new FormData();

    // When uploadUrl is provided, hit that endpoint (e.g. upload_result):
    // POST creates a new result file, PUT edits fit_status / replaces the file.
    if (this.props.uploadUrl) {
      if (attachment.file) formData.append('file', attachment.file);
      formData.append('fit_status', attachment.fit_status || '');

      const submit = () => fetch(this.props.uploadUrl, {
        method: attachment.id ? 'PUT' : 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData
      });

      this.setState({ submitting: true, submitError: null });
      try {
        let response = await submit();
        let result = await response.json();

        // The server refuses files whose name looks like another participant's;
        // uploading anyway needs an explicit confirmation from the operator.
        if (!response.ok && result.error_code === 'name_mismatch') {
          const proceed = window.confirm(`${result.errors}\n\nUpload anyway?`);
          if (!proceed) {
            this.setState({ submitting: false, confirming: false });
            return;
          }
          formData.append('confirm_name_mismatch', 'true');
          response = await submit();
          result = await response.json();
        }

        if (response.ok) {
          this.onClose();
          if (this.props.tableRef.current) this.props.tableRef.current.onQueryChange();
        } else {
          console.error('Error upload result:', result);
          this.setState({ submitError: this.errorText(result) });
        }
      } catch (err) {
        console.error('Error upload result:', err);
        this.setState({ submitError: 'Upload failed. Please try again.' });
      } finally {
        this.setState({ submitting: false });
      }
      return;
    }

    formData.append('profile_id', +attachment.profile_id);
    formData.append('doctor_id', +attachment.doctor_id);
    formData.append('hospital_id', +attachment.hospital_id);
    formData.append('polyclinic', attachment.polyclinic || '');
    formData.append('attachment_number', attachment.attachment_number || '');
    formData.append('assessment_type', attachment.assessment_type || '');
    formData.append('attachable_type', attachment.attachable_type);
    formData.append('attachable_id', +attachment.attachable_id);
    if (attachment.file) formData.append('file', attachment.file);

    let url = this.BASE_URL;
    let method = 'POST';

    if (attachment.id) {
      url += '/' + attachment.id;
      method = 'PUT';
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData
      });

      const result = await response.json();
      console.log('Attachment request result:', result);

      if (response.ok) {
        this.onClose();
        if (this.props.tableRef.current) {
          this.props.tableRef.current.onQueryChange();
        }
      } else {
        console.error('Error request attachment:', result);
      }
    } catch (err) {
      console.error('Error request attachment:', err);
    }
  };

  fetchDoctors = (hospitalId) => {
    return new Promise((resolve, reject) => {
      const url = `/api/doctors${hospitalId ? '?hospital_id=' + hospitalId : ''}`;
      fetch(url)
        .then(async res => {
          if (res.status >= 400) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          resolve(data);
        })
        .catch(error => {
          console.error('Fetch doctors error:', error);
          return null;
        });
    })
  };

  fetchAttachables = (attachableType, profileId) => {
    return new Promise((resolve, reject) => {
      const url = `${this.BASE_URL}/attachables?profile_ids=${profileId}&attachable_type=${attachableType}`;
      fetch(url)
        .then(async res => {
          if (res.status >= 400) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (!data || !data.success) {
            reject('Fetch attachables error: invalid response');
          }
          resolve(data.data || []);
        })
        .catch(error => {
          console.error('Fetch doctors error:', error);
          return null;
        });
    })
  };

  loadDoctorsIfNeeded = async (hospitalId) => {
    if (this.state.doctorsHospitalId === hospitalId) return;

    if (!hospitalId) {
      this.setState({ doctorOptions: [], doctorsHospitalId: hospitalId });
      return;
    }

    const doctors = await this.fetchDoctors(hospitalId);
    const doctorOptions = doctors?.map(doctor => ({value: doctor.id, label: doctor.full_name})) || [];
    this.setState({ doctorOptions, doctorsHospitalId: hospitalId });
  };

  loadHospitalsIfNeeded = async (prevProps) => {
    if (this.props.hospitals !== prevProps?.hospitals) {
      this.setState({ hospitalOptions: this.props.hospitals?.map(([name, id]) => ({value: id, label: name})) || [] });
    }
  };

  loadAttachablesIfNeeded = async (attachableType, profileId) => {
    if (this.state.attachablesType === attachableType && this.state.attachablesProfileId === profileId) return;

    if (!attachableType || !profileId) {
      this.setState({ attachables: [], attachablesType: attachableType, attachablesProfileId: profileId });
      return;
    }

    const attachables = await this.fetchAttachables(attachableType, profileId);
    const attachableOptions = attachables?.map(attachable => ({value: attachable.id, label: attachable.name})) || [];
    this.setState({ attachableOptions, attachablesType: attachableType, attachablesProfileId: profileId });
  };

  loadProfilesIfNeeded = async (prevProps) => {
    if (this.props.profiles !== prevProps?.profiles) {
      this.setState({ profileOptions: this.props.profiles?.map(([name, id]) => ({value: id, label: name})) || [] });
    }
  };

  onConfirm = () => {
    this.setState({ confirming: true });
  };

  onCancel = () => {
    this.setState({ confirming: false, submitError: null });
  };

  onClose = () => {
    this.onCancel();
    this.props.onClose();
  };

  // =====================
  // Render Component
  // =====================
  async componentDidMount() {
    await this.loadDoctorsIfNeeded(this.state.attachment?.hospital_id);
    await this.loadHospitalsIfNeeded();
    await this.loadAttachablesIfNeeded(this.state.attachment?.attachable_type, this.state.attachment?.profile_id);
    await this.loadProfilesIfNeeded();
  }

  async componentDidUpdate(prevProps, prevState) {
    await this.loadDoctorsIfNeeded(this.state.attachment?.hospital_id);
    await this.loadHospitalsIfNeeded(prevProps);
    await this.loadAttachablesIfNeeded(this.state.attachment?.attachable_type, this.state.attachment?.profile_id);
    await this.loadProfilesIfNeeded(prevProps);

    // If attachment prop changes
    if (this.props.attachment !== prevProps.attachment) {
      this.setState({ attachment: {...this.props.attachment} });
    }
  }

  render() {
    return (
      <Modal
        open={this.props.open}
        onClose={this.onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        style={{ overflow: 'auto' }}
      >
        <Fade in={this.props.open} timeout={500}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="#fff"
            p={4}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
              borderRadius: '4px',
              minWidth: '300px'
            }}
          >
            <form onSubmit={this.handleAttachmentRequest}>
              <Typography variant="h6" gutterBottom>
                { this.props.uploadUrl
                  ? `${this.state.attachment?.id > 0 ? 'Edit' : 'Upload'} Result`
                  : `${this.state.attachment?.id > 0 ? 'Edit' : 'Create'} Attachment Data` }
              </Typography>

              { this.state.attachment &&
                <div className="row">
                  {(!this.props.uploadUrl || this.state.attachment.profile_id) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="profile_id">Name *</label>
                      <Select2Wrapper
                        required
                        className="form-control"
                        value={this.state.attachment.profile_id?.toString() || ''}
                        name="profile_id"
                        onChange={(e) => this.handleChange("attachable_id", null, "profile_id", +e.target.value)}
                        useDirectParent={true}
                        options={{
                          placeholder: "Search Profile",
                          disabled: !this.props.editable || this.props.fromAttachable || !!this.props.uploadUrl,
                        }}
                        items={this.state.profileOptions}
                      >
                      </Select2Wrapper><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {(!this.props.uploadUrl || this.state.attachment.hospital_id) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="hospital_id">Hospital *</label>
                      <Select2Wrapper
                        required
                        className="form-control"
                        value={this.state.attachment.hospital_id?.toString() || ''}
                        name="hospital_id"
                        onChange={(e) => this.handleChange("doctor_id", null, "hospital_id", +e.target.value)}
                        useDirectParent={true}
                        options={{
                          placeholder: "Search Hospital Branch",
                          disabled: !this.props.editable || !!this.props.uploadUrl,
                        }}
                        items={this.state.hospitalOptions}
                      >
                      </Select2Wrapper><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {(!this.props.uploadUrl || this.state.attachment.doctor_id) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="doctor_id">Doctor *</label>
                      <Select2Wrapper
                        required
                        className="form-control"
                        value={this.state.attachment.doctor_id?.toString() || ''}
                        name="doctor_id"
                        onChange={(e) => this.handleChange("doctor_id", +e.target.value)}
                        useDirectParent={true}
                        options={{
                          placeholder: "Search Doctor",
                          disabled: !this.props.editable || !!this.props.uploadUrl,
                        }}
                        items={this.state.doctorOptions}
                      >
                      </Select2Wrapper><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {(!this.props.uploadUrl || this.state.attachment.polyclinic) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="polyclinic">Polyclinic</label>
                      <input
                        placeholder="Input Polyclinic"
                        disabled={!this.props.editable || !!this.props.uploadUrl}
                        className="form-control"
                        type="text"
                        value={this.state.attachment.polyclinic || ''}
                        name="polyclinic"
                        onChange={(e) => this.handleChange("polyclinic", e.target.value)}
                      /><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {(!this.props.uploadUrl || this.state.attachment.attachment_number) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="attachment_number">Attachment Number</label>
                      <input
                        placeholder="Input Attachment Number"
                        disabled={!this.props.editable || !!this.props.uploadUrl}
                        className="form-control"
                        type="text"
                        value={this.state.attachment.attachment_number || ''}
                        name="attachment_number"
                        onChange={(e) => this.handleChange("attachment_number", e.target.value)}
                      /><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {(!this.props.uploadUrl || this.state.attachment.assessment_type) &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="assessment_type">Assessment Type</label>
                      <input
                        placeholder="Input Assessment Type"
                        disabled={!this.props.editable || !!this.props.uploadUrl}
                        className="form-control"
                        type="text"
                        value={this.state.attachment.assessment_type || ''}
                        name="assessment_type"
                        onChange={(e) => this.handleChange("assessment_type", e.target.value)}
                      /><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {!this.props.uploadUrl &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="attachable_type">Attachable Type *</label>
                      <Select2Wrapper
                        required
                        className="form-control"
                        value={this.state.attachment.attachable_type?.toString() || ATTACHABLES.EHC_SERVICE_ORDER}
                        name="attachable_type"
                        onChange={(e) => this.handleChange("attachable_id", null, "attachable_type", e.target.value)}
                        useDirectParent={true}
                        options={{
                          placeholder: "Search Attachable Type",
                          disabled: true || !this.props.editable || this.props.fromAttachable, //for now, we do not have another attachable type
                        }}
                        items={ATTACHABLES_OPTIONS}
                      >
                      </Select2Wrapper><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {!this.props.uploadUrl &&
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="attachable_id">Attachable *</label>
                      <Select2Wrapper
                        required
                        className="form-control"
                        value={this.state.attachment.attachable_id?.toString() || ''}
                        name="attachable_id"
                        onChange={(e) => this.handleChange("attachable_id", +e.target.value)}
                        useDirectParent={true}
                        options={{
                          placeholder: "Search Attachable",
                          disabled: !this.props.editable || this.props.fromAttachable,
                        }}
                        items={this.state.attachableOptions}
                      >
                      </Select2Wrapper><i className="form-group__bar"></i>
                    </div>
                  </div>
                  }
                  {this.state.attachment.id > 0 && this.state.attachment.guarantor &&
                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="guarantor">Guarantor</label>
                        <input
                          disabled={true}
                          className="form-control"
                          type="text"
                          value={this.state.attachment.guarantor || ''}
                          name="guarantor"
                        /><i className="form-group__bar"></i>
                      </div>
                    </div>
                  }
                  {this.props.uploadUrl &&
                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="fit_status">Fit Status</label>
                        <select
                          disabled={!this.props.editable}
                          className="form-control"
                          value={this.state.attachment.fit_status || ''}
                          name="fit_status"
                          onChange={(e) => this.handleChange("fit_status", e.target.value)}
                        >
                          <option value="">Select fit status</option>
                          {FIT_STATUS_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <i className="form-group__bar"></i>
                      </div>
                    </div>
                  }
                  <div className="col-12">
                    <div className="form-group">
                      <label htmlFor="file">File *</label>
                      <CustomFileUpload
                        required
                        disabled={!this.props.editable}
                        name="file"
                        onChange={(e) => this.handleChange("file", e.target.files[0], "file_name", e.target.files[0]?.name, "file_url", URL.createObjectURL(e.target.files[0]))}
                        accept={this.props.accept || ".jpg, .jpeg, .gif, .png, .pdf"}
                        fileUrl={this.state.attachment.file_url}
                        fileName={this.state.attachment.file_name}
                      />
                    </div>
                  </div>
                </div>
              }

              <Box className="d-flex justify-content-end">
                {this.props.removable && this.state.attachment?.id && !this.props.uploadUrl &&
                  <Button
                    variant="contained"
                    onClick={this.props.onRemove}
                    disabled={!this.state.attachment || this.state.confirming}
                    className="mr-2"
                    style={{ backgroundColor: '#d93939ff' }}
                  >
                    Delete
                  </Button>
                }
                <Button
                  variant="outlined"
                  disabled={this.state.confirming}
                  onClick={this.onClose}
                  className="mr-2"
                >
                  Cancel
                </Button>
                {this.props.editable &&
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!this.state.attachment || this.state.confirming}
                    className="mr-2"
                    onClick={this.onConfirm}
                  >
                    {this.state.attachment?.id ? 'Update' : 'Create'}
                  </Button>
                }
              </Box>
              {this.state.submitError &&
              <Box className="d-flex justify-content-end mt-2">
                <Typography color="error" variant="body2">{this.state.submitError}</Typography>
              </Box>
              }
              {this.props.editable && this.state.confirming &&
              <Box className="d-flex justify-content-end mt-4">
                <Button
                  variant="outlined"
                  onClick={this.onCancel}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  style={{backgroundColor: "#366602ff", color: "#fff"}}
                  disabled={!this.state.attachment || this.state.submitting}
                  className="mr-2"
                  type="submit"
                >
                  Confirm {this.state.attachment?.id ? 'Update' : 'Create'}
                </Button>
              </Box>
              }
            </form>
          </Box>
        </Fade>
      </Modal>
    );
  }
}
